export type NamedErrorBag<K extends string> = Partial<Record<K, string[]>>;

export interface ErrorBagInterface<K extends string> {
    has(key: K): boolean;
    hasAny(keys?: K | K[]): boolean;
    isEmpty(): boolean;
    forget(keys: K | K[]): void;
    get(key: K): string[] | null;
    message(key: K): string | null;
    first(key: K): string | null;
    add(key: K, error: string): void;
    set(key: K, errors: string[]): void;
    fill(errors: NamedErrorBag<K>): void;
    empty(): void;
    all(): NamedErrorBag<K>;
}

export class ErrorBag<K extends string> implements ErrorBagInterface<K> {
    #errors: NamedErrorBag<K> = {};

    public keys(): K[] {
        return Object.keys(this.#errors) as K[];
    }

    public all(): NamedErrorBag<K> {
        return { ...this.#errors };
    }

    public has(key: K): boolean {
        return Object.hasOwn(this.#errors, key);
    }

    public hasAny(keys?: K | K[]): boolean {
        return keys
            ? (Array.isArray(keys) ? keys : [keys]).some((key) => this.has(key))
            : Object.values(this.#errors).length > 0;
    }

    public isEmpty(): boolean {
        return !this.hasAny();
    }

    public forget(keys: K | K[]) {
        if (Array.isArray(keys)) {
            for (const key of keys) {
                this.forget(key);
            }
        } else if (this.has(keys)) {
            delete this.#errors[keys];
        }

        return this;
    }
    public get(key: K): string[] | null {
        return this.#errors[key] ?? null;
    }

    public message(key: K): string | null {
        return this.get(key)?.join(", ") ?? null;
    }

    public first(key: K): string | null {
        return this.get(key)?.[0] ?? null;
    }

    public add(key: K, error: string) {
        return this.set(key, [...(this.#errors[key] ?? []), error]);
    }

    public set(key: K, errors: string[]) {
        const filtered = errors.filter((v, i, a) => a.indexOf(v) === i && v.trim().length > 0);

        if (filtered.length) {
            this.#errors[key] = filtered;
        } else {
            this.forget(key);
        }

        return this;
    }

    public fill(errors: NamedErrorBag<K>) {
        Object.entries(errors).map(([key, value]) => this.set(key as K, value as string[]));

        return this;
    }

    public empty() {
        this.#errors = {};

        return this;
    }

    public clone(): ErrorBag<K> {
        return new ErrorBag<K>().fill(this.#errors);
    }
}
